import React from 'react';
import {FormProvider, useFieldArray, useForm, useFormContext} from 'react-hook-form';
import type {ProyectoFormDTO, ProyectoRequerimientosDTO} from '../../../../api/Objetos';
import {requerimientosApi} from '../../../../api/api';
import {createEmptyProyecto} from '../emptyProyecto';
import {DateField, FieldGrid, TextField, ToggleField} from '../../../../ui/form/fields';
import {
    FormSection,
    buildCardTitle,
    buildSummary,
    hasPersistedId,
    ItemCard,
    EmptyState,
    useProjectReadOnly,
    BulkToggle
} from '../projectForm.helpers';
import {SectionAlerts, SectionSaveButton, useCollectionSection} from '../sectionCrudUtils';
import {useRegisterSectionSave} from '../SectionSaveCoordinator';
import BarraCargando from "../../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';

export function RequerimientosSection(props: { proyectoId: number; showIds?: boolean }) {
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({defaultValues: createEmptyProyecto()});
    const section = useCollectionSection({
        queryKey: ['admin', 'proyectos', 'requerimientos'],
        proyectoId: props.proyectoId,
        getFn: requerimientosApi.list,
        createFn: requerimientosApi.create,
        updateFn: requerimientosApi.update,
        deleteFn: requerimientosApi.delete,
    });

    React.useEffect(() => {
        form.reset({
            ...createEmptyProyecto(),
            requerimientos: section.data ?? [],
        });
    }, [form, section.data]);

    const handleSave = form.handleSubmit(async (value) => {
        await section.save(value.requerimientos ?? []);
    });

    useRegisterSectionSave({
        id: 'requerimientos',
        isDirty: form.formState.isDirty,
        isSaving: section.saveMutation.isPending,
        save: async () => {
            await handleSave();
        },
    });

    return (
        <FormProvider {...form}>
            <RequerimientosSectionFields 
                showIds={Boolean(props.showIds)}
                isLoading={section.isLoading}
                loadError={section.error}
            />
            {!readOnly && (
                <>
                    <SectionSaveButton onClick={() => void handleSave()} busy={section.saveMutation.isPending}/>
                </>
            )}
        </FormProvider>
    );
}

function RequerimientosSectionFields(props: { showIds: boolean, isLoading: boolean, loadError: any }) {
    const { t } = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const requerimientos = useFieldArray({control: form.control, name: 'requerimientos'});
    const readOnly = useProjectReadOnly();

    const setAllPublic = (isPublic: boolean) => {
        requerimientos.fields.forEach((field, index) => {
            form.setValue(`requerimientos.${index}.publico`, isPublic, { shouldDirty: true });
        });
    };
    const count = requerimientos.fields.length;
    const currentRequerimientos = form.watch('requerimientos') || [];

    return (
        <FormSection
            id="requerimientos"
            title={t('requerimientosSection.title', { count })}
            collapsible
            defaultOpen={false}
            headerActions={!readOnly &&
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {props.isLoading && <BarraCargando size="1.2em" />}
                    <BulkToggle
                        label={t('requerimientosSection.public')}
                        fields={currentRequerimientos}
                        onToggle={setAllPublic}
                    />
                </div>
            }
        >
            <SectionAlerts loadError={props.loadError}/>
            {requerimientos.fields.length === 0 && <EmptyState message={t('requerimientosSection.noRequirements')}/>}
            {requerimientos.fields.map((field, index) => (
                <RequerimientoCard key={field.id} index={index} onRemove={() => requerimientos.remove(index)}
                                   showIds={props.showIds}/>
            ))}

            {!readOnly ? (
                <button
                    className="btn btnSecondary"
                    type="button"
                    onClick={() =>
                        requerimientos.append({
                            id: 0,
                            fecha: new Date().toISOString().slice(0, 10),
                            descripcion: '',
                            observaciones: '',
                            publico: false,
                        })
                    }
                >
                    {t('requerimientosSection.addRequirement')}
                </button>
            ) : null}
        </FormSection>
    );
}

function RequerimientoCard(props: { index: number; onRemove: () => void; showIds: boolean }) {
    const { t } = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const requerimiento = form.watch(`requerimientos.${props.index}` as const) as ProyectoRequerimientosDTO | undefined;
    const readOnly = useProjectReadOnly();
    if (!requerimiento) {
        return null;
    }

    return (
        <ItemCard
            key={requerimiento.id}
            title={buildCardTitle(t, t('requerimientosSection.requirement'), props.index, requerimiento.descripcion, requerimiento.observaciones, requerimiento.fecha)}
            summary={buildSummary(t, requerimiento.fecha, requerimiento.descripcion, requerimiento.observaciones)}
            onRemove={props.onRemove}
            defaultOpen={!hasPersistedId(requerimiento.id)}
            actions={!readOnly &&
                <ToggleField
                    label={t('requerimientosSection.public')}
                    name={`requerimientos.${props.index}.publico`}
                    register={form.register}
                />
            }
        >
            <FieldGrid>
                {props.showIds && hasPersistedId(requerimiento.id) ? (
                    <div className="label">
                        {t('requerimientosSection.id')}
                        <input className="input" value={requerimiento.id} readOnly/>
                    </div>
                ) : null}
                <DateField label={t('requerimientosSection.date')} name={`requerimientos.${props.index}.fecha`} register={form.register}/>
                <TextField label={t('requerimientosSection.description')} name={`requerimientos.${props.index}.descripcion`}
                           register={form.register}/>
                <TextField label={t('requerimientosSection.observations')} name={`requerimientos.${props.index}.observaciones`}
                           register={form.register}/>
            </FieldGrid>
        </ItemCard>
    );
}
